import React, { useState, useEffect, createContext, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged,
  signOut 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  where,
  getDocs
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

// Firebase configuration - Replace with your config
const firebaseConfig = {
  apiKey: "AIzaSyDKVBFYwPJ_vUaGhZZf9s7NHc6wOUo1fnY",
  authDomain: "owningup-1425f.firebaseapp.com",
  projectId: "owningup-1425f",
  storageBucket: "owningup-1425f.firebasestorage.app",
  messagingSenderId: "1034414751016",
  appId: "1:1034414751016:web:24e566f1889d394319a41d"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// Initialize the chat function
const chatWithAI = httpsCallable(functions, 'chat');

// Context for tenant and user data
const AppContext = createContext(null);

const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};

// Generate a unique tenant ID
const generateTenantId = () => {
  return 'tenant_' + Math.random().toString(36).substr(2, 12);
};

// Generate invite code
const generateInviteCode = () => {
  return Math.random().toString(36).substr(2, 8).toUpperCase();
};

// ============================================
// LOGIN SCREEN
// ============================================
const LoginScreen = ({ onLogin, error, loading }) => {
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/logo.png" alt="OwningUp" className="logo-image" />
          <p className="app-subtitle">SEL Teacher Support Portal</p>
        </div>
        
        <div className="login-content">
          <p className="login-description">
            A warm, supportive space for educators implementing 
            social-emotional learning curriculum.
          </p>
          
          <button 
            className="google-login-btn" 
            onClick={onLogin}
            disabled={loading}
          >
            {loading ? (
              <div className="loading-spinner small"></div>
            ) : (
              <svg className="google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            <span>{loading ? 'Signing in...' : 'Sign in with Google'}</span>
          </button>
          
          {error && (
            <div className="error-message">
              <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{error}</span>
            </div>
          )}
        </div>
        
        <div className="login-footer">
          <p className="footer-quote">
            "You don't teach people or give them advice without listening 
            and understanding their experiences first."
          </p>
          <p className="footer-attribution">— Rosalind Wiseman</p>
        </div>
      </div>
      
      <div className="login-background">
        <div className="bg-shape bg-shape-1"></div>
        <div className="bg-shape bg-shape-2"></div>
        <div className="bg-shape bg-shape-3"></div>
      </div>
    </div>
  );
};

// ============================================
// ONBOARDING SCREEN - New User Setup
// ============================================
const OnboardingScreen = ({ user, onComplete, onLogout }) => {
  const [step, setStep] = useState('choose'); // 'choose', 'create', 'join'
  const [orgName, setOrgName] = useState('');
  const [orgDomain, setOrgDomain] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [matchedTenant, setMatchedTenant] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(true);

  // Check if user's email domain matches an existing tenant
  useEffect(() => {
    const checkEmailDomain = async () => {
      const emailDomain = user.email.split('@')[1];
      
      try {
        const tenantsRef = collection(db, 'tenants');
        // Check both old 'domain' field and new 'domains' array
        const q = query(tenantsRef, where('domains', 'array-contains', emailDomain));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const tenantDoc = snapshot.docs[0];
          setMatchedTenant({ id: tenantDoc.id, ...tenantDoc.data() });
        } else {
          // Fallback: check old single 'domain' field for backwards compatibility
          const qOld = query(tenantsRef, where('domain', '==', emailDomain));
          const snapshotOld = await getDocs(qOld);
          if (!snapshotOld.empty) {
            const tenantDoc = snapshotOld.docs[0];
            setMatchedTenant({ id: tenantDoc.id, ...tenantDoc.data() });
          }
        }
      } catch (err) {
        console.error('Error checking domain:', err);
      }
      
      setCheckingDomain(false);
    };
    
    checkEmailDomain();
  }, [user.email]);

  const createOrganization = async () => {
    if (!orgName.trim()) {
      setError('Please enter an organization name');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const tenantId = generateTenantId();
      const inviteCode = generateInviteCode();
      
      // Create tenant document
      await setDoc(doc(db, 'tenants', tenantId), {
        name: orgName.trim(),
        domains: orgDomain.trim() ? [orgDomain.trim()] : [],
        inviteCode: inviteCode,
        plan: 'trial',
        planExpiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days trial
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        adminEmails: [user.email]
      });
      
      // Create user document
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        tenantId: tenantId,
        role: 'admin',
        createdAt: serverTimestamp()
      });
      
      onComplete({ tenantId, role: 'admin' });
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create organization. Please try again.');
    }
    
    setLoading(false);
  };

  const joinWithCode = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Find tenant by invite code
      const tenantsRef = collection(db, 'tenants');
      const q = query(tenantsRef, where('inviteCode', '==', inviteCode.trim().toUpperCase()));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setError('Invalid invite code. Please check and try again.');
        setLoading(false);
        return;
      }
      
      const tenantDoc = snapshot.docs[0];
      const tenantId = tenantDoc.id;
      
      // Create user document
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        tenantId: tenantId,
        role: 'teacher', // Default role for invite code joins
        createdAt: serverTimestamp()
      });
      
      onComplete({ tenantId, role: 'teacher' });
    } catch (err) {
      console.error('Error joining organization:', err);
      setError('Failed to join organization. Please try again.');
    }
    
    setLoading(false);
  };

  const joinMatchedTenant = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Create user document
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        tenantId: matchedTenant.id,
        role: 'teacher', // Default role for domain joins
        createdAt: serverTimestamp()
      });
      
      onComplete({ tenantId: matchedTenant.id, role: 'teacher' });
    } catch (err) {
      console.error('Error joining organization:', err);
      setError('Failed to join organization. Please try again.');
    }
    
    setLoading(false);
  };

  if (checkingDomain) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Setting up your account...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <img src="/logo.png" alt="OwningUp" className="logo-image small" />
          <h1>Welcome to OwningUp</h1>
          <p>Hi {user.displayName?.split(' ')[0] || 'there'}! Let's get you set up.</p>
        </div>

        {/* Domain Match Found */}
        {matchedTenant && step === 'choose' && (
          <div className="domain-match-card">
            <div className="match-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h3>We found your organization!</h3>
            <p>Your email matches <strong>{matchedTenant.name}</strong></p>
            <button 
              className="btn-primary"
              onClick={joinMatchedTenant}
              disabled={loading}
            >
              {loading ? 'Joining...' : `Join ${matchedTenant.name}`}
            </button>
            <button 
              className="btn-text"
              onClick={() => setStep('choose')}
            >
              Or set up differently
            </button>
          </div>
        )}

        {/* Choose Path */}
        {(step === 'choose' && !matchedTenant) && (
          <div className="onboarding-options">
            <button 
              className="option-card"
              onClick={() => setStep('create')}
            >
              <div className="option-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3>Create Organization</h3>
              <p>Set up a new school or district account</p>
            </button>
            
            <button 
              className="option-card"
              onClick={() => setStep('join')}
            >
              <div className="option-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
              </div>
              <h3>Join Organization</h3>
              <p>I have an invite code from my school</p>
            </button>
          </div>
        )}

        {/* Create Organization Form */}
        {step === 'create' && (
          <div className="onboarding-form">
            <button className="back-btn" onClick={() => setStep('choose')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Back
            </button>
            
            <h2>Create Your Organization</h2>
            <p>Set up OwningUp for your school or district</p>
            
            <div className="form-group">
              <label htmlFor="orgName">Organization Name *</label>
              <input
                id="orgName"
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="e.g., Lincoln Middle School"
                autoFocus
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="orgDomain">
                Email Domain <span className="optional">(optional)</span>
              </label>
              <input
                id="orgDomain"
                type="text"
                value={orgDomain}
                onChange={(e) => setOrgDomain(e.target.value)}
                placeholder="e.g., lincolnms.edu"
              />
              <span className="help-text">
                Users with this email domain can auto-join your organization
              </span>
            </div>
            
            {error && (
              <div className="error-message">
                <span>{error}</span>
              </div>
            )}
            
            <button 
              className="btn-primary full-width"
              onClick={createOrganization}
              disabled={loading || !orgName.trim()}
            >
              {loading ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        )}

        {/* Join Organization Form */}
        {step === 'join' && (
          <div className="onboarding-form">
            <button className="back-btn" onClick={() => setStep('choose')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Back
            </button>
            
            <h2>Join Your Organization</h2>
            <p>Enter the invite code from your administrator</p>
            
            <div className="form-group">
              <label htmlFor="inviteCode">Invite Code</label>
              <input
                id="inviteCode"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="e.g., ABC12345"
                className="invite-code-input"
                autoFocus
              />
            </div>
            
            {error && (
              <div className="error-message">
                <span>{error}</span>
              </div>
            )}
            
            <button 
              className="btn-primary full-width"
              onClick={joinWithCode}
              disabled={loading || !inviteCode.trim()}
            >
              {loading ? 'Joining...' : 'Join Organization'}
            </button>
          </div>
        )}

        <div className="onboarding-footer">
          <button className="btn-text" onClick={onLogout}>
            Sign in with a different account
          </button>
        </div>
      </div>
      
      <div className="login-background">
        <div className="bg-shape bg-shape-1"></div>
        <div className="bg-shape bg-shape-2"></div>
        <div className="bg-shape bg-shape-3"></div>
      </div>
    </div>
  );
};

// ============================================
// HEADER
// ============================================
const Header = ({ onLogout, onMobileMenuToggle, activePage, onNavigate }) => {
  const { userData, tenantData } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  
  return (
    <header className="app-header">
      <div className="header-content">
        <div className="header-left">
          <button className="mobile-menu-btn" onClick={onMobileMenuToggle}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <img src="/logo.png" alt="OwningUp" className="header-logo-img" />
          <span className="header-org">{tenantData?.name}</span>
        </div>
        
        <nav className="header-nav">
          <a 
            href="#dashboard" 
            className={`nav-link ${activePage === 'dashboard' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); onNavigate('dashboard'); }}
          >
            Dashboard
          </a>
          <a href="#resources" className="nav-link">Resources</a>
          <a href="#sessions" className="nav-link">Sessions</a>
          {(userData?.role === 'admin' || userData?.role === 'counselor') && (
            <a 
              href="#team" 
              className={`nav-link ${activePage === 'team' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); onNavigate('team'); }}
            >
              Team
            </a>
          )}
          {userData?.role === 'admin' && (
            <a href="#settings" className="nav-link">Settings</a>
          )}
        </nav>
        
        <div className="header-right">
          <div className="role-badge">{userData?.role}</div>
          <div className="user-menu" onClick={() => setMenuOpen(!menuOpen)}>
            <img 
              src={userData?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userData?.displayName || userData?.email)}&background=0f3b60&color=fff`} 
              alt={userData?.displayName} 
              className="user-avatar"
            />
            <span className="user-name">{userData?.displayName?.split(' ')[0] || 'User'}</span>
            <svg className={`chevron-icon ${menuOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
          
          {menuOpen && (
            <div className="dropdown-menu">
              <div className="dropdown-header">
                <p className="dropdown-name">{userData?.displayName}</p>
                <p className="dropdown-email">{userData?.email}</p>
                <p className="dropdown-role">{userData?.role} • {tenantData?.name}</p>
              </div>
              <div className="dropdown-divider"></div>
              <button className="dropdown-item" onClick={onLogout}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// ============================================
// CONVERSATION COMPONENTS
// ============================================
const ConversationCard = ({ conversation, onSelect, isSelected }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate();
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const { user, userData } = useApp();
  const isOwnConversation = conversation.userId === user?.uid;
  
  return (
    <div 
      className={`conversation-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(conversation)}
    >
      <div className="conversation-header">
        <span className="conversation-topic">{conversation.topic || 'General Support'}</span>
        <span className="conversation-time">{formatDate(conversation.updatedAt)}</span>
      </div>
      {!isOwnConversation && (
        <p className="conversation-author">{conversation.userName}</p>
      )}
      <p className="conversation-preview">
        {conversation.lastMessage || 'Start a new conversation...'}
      </p>
      <div className="conversation-meta">
        <span className={`status-badge ${conversation.status || 'active'}`}>
          {conversation.status || 'Active'}
        </span>
      </div>
    </div>
  );
};

const ChatMessage = ({ message, isOwn }) => {
  const isAI = message.senderId === 'ai-assistant';
  
  return (
    <div className={`chat-message ${isOwn ? 'own' : 'other'} ${isAI ? 'ai-message' : ''}`}>
      {!isOwn && (
        <div className="message-sender">
          {isAI && <span className="ai-badge">AI</span>}
          {message.senderName}
        </div>
      )}
      <div className={`message-bubble ${isAI ? 'ai-bubble' : ''}`}>
        <p className="message-text">{message.text}</p>
        {message.sources && message.sources.length > 0 && (
          <div className="message-sources">
            <span className="sources-label">Sources:</span>
            {message.sources.map((source, i) => (
              <span key={i} className="source-tag">
                {source.session > 0 ? `Session ${source.session}` : source.title}
              </span>
            ))}
          </div>
        )}
        <span className="message-time">
          {message.timestamp?.toDate?.()?.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit' 
          }) || 'Sending...'}
        </span>
      </div>
    </div>
  );
};

// ============================================
// ADMIN PANEL
// ============================================
// ============================================
// TEAM PAGE
// ============================================
const TeamPage = ({ onBack }) => {
  const { user, tenantData, userData } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    if (!userData?.tenantId) return;
    
    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('tenantId', '==', userData.tenantId));
        const snapshot = await getDocs(q);
        const usersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(usersList);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
      setLoading(false);
    };
    
    fetchUsers();
  }, [userData]);

  const updateUserRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err) {
      console.error('Error updating role:', err);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(tenantData?.inviteCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const activeUsers = users.filter(u => u.status !== 'pending');
  const pendingUsers = users.filter(u => u.status === 'pending');

  return (
    <div className="team-page">
      <div className="team-header">
        <button className="back-btn" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Dashboard
        </button>
        <h1>Team Management</h1>
        <p>{tenantData?.name}</p>
      </div>

      {/* Invite Section */}
      <div className="team-section invite-section">
        <div className="section-header">
          <h2>Invite Team Members</h2>
          <p>Share this code with teachers to let them join your organization</p>
        </div>
        <div className="invite-code-card">
          <div className="invite-code-display-large">
            {showInviteCode ? (
              <code>{tenantData?.inviteCode}</code>
            ) : (
              <code>••••••••</code>
            )}
          </div>
          <div className="invite-actions">
            <button 
              className="btn-secondary"
              onClick={() => setShowInviteCode(!showInviteCode)}
            >
              {showInviteCode ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                  Hide
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Show
                </>
              )}
            </button>
            <button 
              className="btn-primary"
              onClick={copyInviteCode}
            >
              {copiedCode ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy Code
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Active Team Members */}
      <div className="team-section">
        <div className="section-header">
          <h2>Active Team Members</h2>
          <span className="member-count">{activeUsers.length} member{activeUsers.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="loading-spinner"></div>
        ) : activeUsers.length === 0 ? (
          <div className="empty-state">
            <p>No active team members yet. Share your invite code to get started!</p>
          </div>
        ) : (
          <div className="team-grid">
            {activeUsers.map(member => (
              <div key={member.id} className="team-member-card">
                <img 
                  src={member.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.displayName || member.email)}&background=0f3b60&color=fff`}
                  alt={member.displayName}
                  className="member-avatar"
                />
                <div className="member-info">
                  <h3>{member.displayName || 'Unknown'}</h3>
                  <p>{member.email}</p>
                  <span className={`role-badge ${member.role}`}>{member.role}</span>
                </div>
                {userData?.role === 'admin' && member.id !== user?.uid && (
                  <select
                    value={member.role}
                    onChange={(e) => updateUserRole(member.id, e.target.value)}
                    className="role-select"
                  >
                    <option value="teacher">Teacher</option>
                    <option value="counselor">Counselor</option>
                    <option value="admin">Admin</option>
                  </select>
                )}
                {member.id === user?.uid && (
                  <span className="you-badge">You</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Invites */}
      {pendingUsers.length > 0 && (
        <div className="team-section">
          <div className="section-header">
            <h2>Pending Invites</h2>
            <span className="member-count pending">{pendingUsers.length} pending</span>
          </div>
          <div className="team-grid">
            {pendingUsers.map(member => (
              <div key={member.id} className="team-member-card pending">
                <div className="member-avatar-placeholder">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div className="member-info">
                  <h3>{member.email}</h3>
                  <p>Invitation sent</p>
                  <span className="status-badge pending">Pending</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPanel = () => {
  const { user: authUser, tenantData, userData } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteCode, setShowInviteCode] = useState(false);

  useEffect(() => {
    if (userData?.role !== 'admin') return;
    
    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('tenantId', '==', userData.tenantId));
        const snapshot = await getDocs(q);
        const usersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(usersList);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
      setLoading(false);
    };
    
    fetchUsers();
  }, [userData]);

  const updateUserRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err) {
      console.error('Error updating role:', err);
    }
  };

  if (userData?.role !== 'admin') {
    return null;
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Organization Settings</h2>
        <p>{tenantData?.name}</p>
      </div>

      <div className="admin-section">
        <h3>Invite Code</h3>
        <p>Share this code with teachers to let them join your organization</p>
        <div className="invite-code-display">
          {showInviteCode ? (
            <code>{tenantData?.inviteCode}</code>
          ) : (
            <code>••••••••</code>
          )}
          <button 
            className="btn-icon"
            onClick={() => setShowInviteCode(!showInviteCode)}
          >
            {showInviteCode ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
          <button 
            className="btn-icon"
            onClick={() => navigator.clipboard.writeText(tenantData?.inviteCode)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="admin-section">
        <h3>Team Members ({users.length})</h3>
        {loading ? (
          <div className="loading-spinner"></div>
        ) : (
          <div className="users-list">
            {users.map(member => (
              <div key={member.id} className="user-row">
                <img 
                  src={member.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.displayName)}&background=0f3b60&color=fff`}
                  alt={member.displayName}
                  className="user-row-avatar"
                />
                <div className="user-row-info">
                  <p className="user-row-name">{member.displayName}</p>
                  <p className="user-row-email">{member.email}</p>
                </div>
                <select
                  value={member.role}
                  onChange={(e) => updateUserRole(member.id, e.target.value)}
                  disabled={member.id === authUser?.uid}
                  className="role-select"
                >
                  <option value="teacher">Teacher</option>
                  <option value="counselor">Counselor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="admin-section">
        <h3>Plan</h3>
        <div className="plan-info">
          <span className={`plan-badge ${tenantData?.plan}`}>{tenantData?.plan}</span>
          {tenantData?.planExpiresAt && (
            <span className="plan-expires">
              Expires: {tenantData.planExpiresAt.toDate?.()?.toLocaleDateString() || 'N/A'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// DASHBOARD
// ============================================
const Dashboard = () => {
  const { user, userData, tenantData, handleLogout } = useApp();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiThinking, setAiThinking] = useState(false);
  const [activeView, setActiveView] = useState('conversations'); // 'conversations', 'admin'
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activePage, setActivePage] = useState('dashboard'); // 'dashboard', 'team'

  // Close mobile sidebar when selecting a conversation
  const handleSelectConversation = (convo) => {
    setSelectedConversation(convo);
    setMobileSidebarOpen(false);
  };

  // Toggle mobile sidebar
  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  // Handle navigation
  const handleNavigate = (page) => {
    setActivePage(page);
    setMobileSidebarOpen(false);
  };

  // Fetch conversations based on role
  useEffect(() => {
    if (!userData?.tenantId) return;

    let q;
    
    if (userData.role === 'admin' || userData.role === 'counselor') {
      // Admins and counselors see all conversations in their tenant
      q = query(
        collection(db, 'tenants', userData.tenantId, 'conversations'),
        orderBy('updatedAt', 'desc')
      );
    } else {
      // Teachers only see their own conversations
      q = query(
        collection(db, 'tenants', userData.tenantId, 'conversations'),
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc')
      );
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setConversations(convos);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [user.uid, userData]);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversation || !userData?.tenantId) {
      setMessages([]);
      return;
    }
    
    const q = query(
      collection(db, 'tenants', userData.tenantId, 'conversations', selectedConversation.id, 'messages'),
      orderBy('timestamp', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
    });
    
    return () => unsubscribe();
  }, [selectedConversation, userData]);

  const createConversation = async () => {
    if (!newTopic.trim()) return;
    
    try {
      const docRef = await addDoc(
        collection(db, 'tenants', userData.tenantId, 'conversations'), 
        {
          userId: user.uid,
          userEmail: user.email,
          userName: user.displayName,
          topic: newTopic,
          status: 'active',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastMessage: ''
        }
      );
      
      setNewTopic('');
      setShowNewConversation(false);
      setSelectedConversation({ id: docRef.id, topic: newTopic });
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || aiThinking) return;
    
    const messageText = newMessage;
    setNewMessage('');
    setAiThinking(true);
    
    try {
      // 1. Save user's message to Firestore
      await addDoc(
        collection(db, 'tenants', userData.tenantId, 'conversations', selectedConversation.id, 'messages'), 
        {
          text: messageText,
          senderId: user.uid,
          senderName: user.displayName,
          senderRole: userData.role,
          timestamp: serverTimestamp()
        }
      );
      
      // 2. Update conversation's last message
      await updateDoc(
        doc(db, 'tenants', userData.tenantId, 'conversations', selectedConversation.id), 
        {
          lastMessage: messageText.substring(0, 100),
          updatedAt: serverTimestamp()
        }
      );
      
      // 3. Build conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.senderId === 'ai-assistant' ? 'assistant' : 'user',
        content: msg.text
      }));
      
      // 4. Call the AI function
      const response = await chatWithAI({
        message: messageText,
        conversationHistory: conversationHistory
      });
      
      // 5. Save AI response to Firestore
      if (response.data && response.data.message) {
        await addDoc(
          collection(db, 'tenants', userData.tenantId, 'conversations', selectedConversation.id, 'messages'), 
          {
            text: response.data.message,
            senderId: 'ai-assistant',
            senderName: 'SEL Counselor',
            senderRole: 'assistant',
            sources: response.data.sources || [],
            timestamp: serverTimestamp()
          }
        );
        
        // Update conversation's last message with AI response
        await updateDoc(
          doc(db, 'tenants', userData.tenantId, 'conversations', selectedConversation.id), 
          {
            lastMessage: response.data.message.substring(0, 100),
            updatedAt: serverTimestamp()
          }
        );
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Save error message so user knows something went wrong
      await addDoc(
        collection(db, 'tenants', userData.tenantId, 'conversations', selectedConversation.id, 'messages'), 
        {
          text: "I'm sorry, I encountered an error processing your message. Please try again.",
          senderId: 'ai-assistant',
          senderName: 'SEL Counselor',
          senderRole: 'assistant',
          isError: true,
          timestamp: serverTimestamp()
        }
      );
    } finally {
      setAiThinking(false);
    }
  };

  // Show Team Page
  if (activePage === 'team') {
    return (
      <>
        <Header 
          onLogout={handleLogout} 
          onMobileMenuToggle={toggleMobileSidebar}
          activePage={activePage}
          onNavigate={handleNavigate}
        />
        <TeamPage onBack={() => setActivePage('dashboard')} />
      </>
    );
  }

  return (
    <>
      <Header 
        onLogout={handleLogout} 
        onMobileMenuToggle={toggleMobileSidebar}
        activePage={activePage}
        onNavigate={handleNavigate}
      />
      <main className="dashboard">
        {/* Mobile backdrop */}
        <div 
          className={`mobile-backdrop ${mobileSidebarOpen ? 'visible' : ''}`}
          onClick={() => setMobileSidebarOpen(false)}
        />
        
        <div className={`dashboard-sidebar ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
          {/* Mobile Navigation Links */}
          <div className="mobile-nav">
            <button 
              className={`mobile-nav-link ${activePage === 'dashboard' ? 'active' : ''}`}
              onClick={() => { handleNavigate('dashboard'); setMobileSidebarOpen(false); }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              Dashboard
            </button>
            {(userData?.role === 'admin' || userData?.role === 'counselor') && (
              <button 
                className={`mobile-nav-link ${activePage === 'team' ? 'active' : ''}`}
                onClick={() => { handleNavigate('team'); setMobileSidebarOpen(false); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Team
              </button>
            )}
          </div>
          
          <div className="mobile-nav-divider"></div>
          
          <div className="sidebar-header">
            <h2 className="sidebar-title">
              {activeView === 'conversations' ? 'Conversations' : 'Settings'}
            </h2>
            {activeView === 'conversations' && (
              <button 
                className="new-conversation-btn"
                onClick={() => setShowNewConversation(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            )}
          </div>

        {/* View Toggle for Admins */}
        {userData?.role === 'admin' && (
          <div className="sidebar-tabs">
            <button 
              className={`sidebar-tab ${activeView === 'conversations' ? 'active' : ''}`}
              onClick={() => setActiveView('conversations')}
            >
              Conversations
            </button>
            <button 
              className={`sidebar-tab ${activeView === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveView('admin')}
            >
              Settings
            </button>
          </div>
        )}
        
        {activeView === 'conversations' && (
          <>
            {showNewConversation && (
              <div className="new-conversation-form">
                <input
                  type="text"
                  placeholder="What's on your mind?"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  className="topic-input"
                  autoFocus
                />
                <div className="form-actions">
                  <button 
                    className="btn-cancel"
                    onClick={() => setShowNewConversation(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn-create"
                    onClick={createConversation}
                    disabled={!newTopic.trim()}
                  >
                    Start
                  </button>
                </div>
              </div>
            )}
            
            <div className="conversations-list">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading conversations...</p>
                </div>
              ) : conversations.length === 0 ? (
                <div className="empty-state">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <p>No conversations yet</p>
                  <span>Start one to get support</span>
                </div>
              ) : (
                conversations.map(convo => (
                  <ConversationCard
                    key={convo.id}
                    conversation={convo}
                    onSelect={handleSelectConversation}
                    isSelected={selectedConversation?.id === convo.id}
                  />
                ))
              )}
            </div>
          </>
        )}

        {activeView === 'admin' && <AdminPanel />}
      </div>
      
      <div className="dashboard-main">
        {selectedConversation ? (
          <>
            <div className="chat-header">
              <div className="chat-header-info">
                <h3 className="chat-topic">{selectedConversation.topic}</h3>
                <span className="chat-status">
                  {selectedConversation.userName && selectedConversation.userId !== user.uid 
                    ? `${selectedConversation.userName} • ` 
                    : ''}
                  Active conversation
                </span>
              </div>
              <button className="chat-options-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="1"/>
                  <circle cx="19" cy="12" r="1"/>
                  <circle cx="5" cy="12" r="1"/>
                </svg>
              </button>
            </div>
            
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="chat-welcome">
                  <div className="welcome-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                      <line x1="9" y1="9" x2="9.01" y2="9"/>
                      <line x1="15" y1="9" x2="15.01" y2="9"/>
                    </svg>
                  </div>
                  <h4>Welcome to your conversation</h4>
                  <p>
                    This is a safe space to share what's on your mind. 
                    Whether you need to vent, want practical advice, or 
                    just need someone to listen—we're here for you.
                  </p>
                  <div className="welcome-prompts">
                    <button onClick={() => setNewMessage("I'm feeling overwhelmed with...")}>
                      I'm feeling overwhelmed
                    </button>
                    <button onClick={() => setNewMessage("I need advice about...")}>
                      I need advice
                    </button>
                    <button onClick={() => setNewMessage("A student situation...")}>
                      Student situation
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map(msg => (
                    <ChatMessage
                      key={msg.id}
                      message={msg}
                      isOwn={msg.senderId === user.uid}
                    />
                  ))}
                  {aiThinking && (
                    <div className="ai-thinking-indicator">
                      <div className="ai-thinking-content">
                        <span className="ai-badge">AI</span>
                        <span className="thinking-text">SEL Counselor is thinking</span>
                        <div className="thinking-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <form className="chat-input-form" onSubmit={sendMessage}>
              <div className="input-wrapper">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Share what's on your mind..."
                  rows={1}
                  disabled={aiThinking}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(e);
                    }
                  }}
                />
                <button 
                  type="submit" 
                  className="send-btn"
                  disabled={!newMessage.trim() || aiThinking}
                >
                  {aiThinking ? (
                    <div className="loading-spinner small"></div>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="no-selection">
            <div className="no-selection-content">
              <div className="no-selection-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
              </div>
              <h3>Select a conversation</h3>
              <p>Choose an existing conversation or start a new one to get support.</p>
              <button 
                className="start-conversation-btn"
                onClick={() => setShowNewConversation(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Start a conversation
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="dashboard-aside">
        <div className="aside-card resources-card">
          <h3 className="aside-title">Quick Resources</h3>
          <ul className="resource-list">
            <li>
              <a href="#guides" className="resource-link">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                Session Guides
              </a>
            </li>
            <li>
              <a href="#faq" className="resource-link">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                FAQ
              </a>
            </li>
            <li>
              <a href="#best-practices" className="resource-link">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                Best Practices
              </a>
            </li>
          </ul>
        </div>
        
        <div className="aside-card quote-card">
          <blockquote>
            "Students can learn when they feel uncomfortable but not unsafe."
          </blockquote>
          <cite>— Owning Up Curriculum</cite>
        </div>
        
        <div className="aside-card tips-card">
          <h3 className="aside-title">Today's Tip</h3>
          <p>
            Before teaching difficult topics, take a moment to "check your baggage" — 
            reflect on your own experiences that might affect how you show up.
          </p>
        </div>
      </div>
    </main>
    </>
  );
};

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Check if user's email is in the allowlist
        try {
          const allowlistDoc = await getDoc(doc(db, 'config', 'allowlist'));
          const allowedEmails = allowlistDoc.exists() ? allowlistDoc.data().emails || [] : [];
          
          if (allowedEmails.length > 0 && !allowedEmails.includes(currentUser.email.toLowerCase())) {
            // Email not in allowlist - sign them out
            await signOut(auth);
            setError('Access denied. Your email is not authorized to use this application.');
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Error checking allowlist:', err);
          // If we can't check the allowlist, deny access for safety
          await signOut(auth);
          setError('Unable to verify access. Please try again.');
          setLoading(false);
          return;
        }
        
        setUser(currentUser);
        
        // Check if user exists in database
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData({ uid: currentUser.uid, ...data });
            
            // Fetch tenant data
            const tenantDoc = await getDoc(doc(db, 'tenants', data.tenantId));
            if (tenantDoc.exists()) {
              setTenantData({ id: tenantDoc.id, ...tenantDoc.data() });
            }
            
            setNeedsOnboarding(false);
          } else {
            // New user - needs onboarding
            setNeedsOnboarding(true);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError('Failed to load user data');
        }
      } else {
        setUser(null);
        setUserData(null);
        setTenantData(null);
        setNeedsOnboarding(false);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    try {
      setError(null);
      setLoading(true);
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to sign in. Please try again.');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleOnboardingComplete = async ({ tenantId, role }) => {
    // Refresh user and tenant data
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      setUserData({ uid: user.uid, ...userDoc.data() });
    }
    
    const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
    if (tenantDoc.exists()) {
      setTenantData({ id: tenantDoc.id, ...tenantDoc.data() });
    }
    
    setNeedsOnboarding(false);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <img src="/logo-icon.png" alt="OwningUp" className="loading-logo-img" />
          <div className="loading-spinner large"></div>
          <p>Loading OwningUp...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <LoginScreen onLogin={handleLogin} error={error} loading={loading} />;
  }

  // Logged in but needs onboarding
  if (needsOnboarding) {
    return (
      <OnboardingScreen 
        user={user} 
        onComplete={handleOnboardingComplete}
        onLogout={handleLogout}
      />
    );
  }

  // Fully authenticated and onboarded
  return (
    <AppContext.Provider value={{ user, userData, tenantData, handleLogout }}>
      <div className="app">
        <Dashboard />
      </div>
    </AppContext.Provider>
  );
}

export default App;